// components/account/Navbar.jsx - ULTRA FIXED version with NO infinite loops

"use client";

import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { createPortal } from "react-dom";
import SearchModal from "../common/SearchModal";
import LogoutConfirmationModal from "../modals/LogoutConfirmModal";
import NotificationDropdown from "./NotificationDropdown";
import ChatDropdown from "./ChatDropdown";
import SupportModal from "../modals/SupportModal";
import AIChatWidget from "../chat/AIChatWidget";
import { useAuth } from "../../hooks/useAuth"; // UNIFIED AUTHENTICATION
import { handleApiError } from "../../utils/apiUtils";
import { getUserPlaceholderImage } from "../../utils/imageUtils";
import { useTranslation } from "react-i18next";
import { useHamburgerMenu } from "../../hooks/useHamburgerMenu";
import api from "../../services/api";
import { notificationsAPI, userAPI } from "../../services/api";
import "./Navbar.css";
import { USER_TYPES } from "../../constants/userTypes";

// Language Toggle Component
const LanguageToggle = () => {
  const { i18n, t } = useTranslation();
  const [isHovered, setIsHovered] = useState(false);

  const toggleLanguage = () => {
    const newLang = i18n.language === "en" ? "ar" : "en";
    i18n.changeLanguage(newLang);
  };

  return (
    <button
      onClick={toggleLanguage}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className={`navbar-language-toggle ${isHovered ? 'navbar-language-toggle-hovered' : ''}`}
      title={
        i18n.language === "en"
          ? t("language.switchToArabic")
          : t("language.switchToEnglish")
      }
    >
      {i18n.language === "en" ? "AR" : "EN"}
    </button>
  );
};

// Icon Component - same as before
const IconComponent = ({
  iconName,
  isActive,
  isHovered,
  colors,
  defaultColor = "rgba(73, 73, 73, 0.6)",
}) => {
  const iconColor = isHovered || isActive ? colors.primary : defaultColor;

  const getIconSvg = (name, color) => {
    switch (name) {
      case "social-media":
        return (
          <svg
            width="28"
            height="28"
            viewBox="0 0 20 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M18.3346 6.66659V11.5083C17.6013 11.0833 16.7513 10.8333 15.8346 10.8333C13.0763 10.8333 10.8346 13.0749 10.8346 15.8333C10.8346 16.1166 10.868 16.3916 10.9096 16.6666H3.33464C2.41797 16.6666 1.66797 15.9249 1.66797 14.9999V4.99992C1.66797 4.07492 2.40964 3.33325 3.33464 3.33325H8.33464L10.0013 4.99992H16.668C17.5846 4.99992 18.3346 5.74159 18.3346 6.66659ZM14.168 18.3333L18.3346 15.8333L14.168 13.3333V18.3333Z"
              fill={color}
            />
          </svg>
        );
      case "my-pets":
        return (
          <svg
            width="28"
            height="28"
            viewBox="0 0 20 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M6.95858 2.50008C7.94192 2.35842 8.98358 3.43342 9.28358 4.91675C9.58358 6.39175 9.04191 7.70842 8.05858 7.85842C7.08358 8.00842 6.03358 6.93342 5.72525 5.45008C5.41691 3.97508 5.97525 2.65842 6.95858 2.50008ZM12.9169 2.50008C13.9086 2.65842 14.4586 3.97508 14.1669 5.45008C13.8502 6.93342 12.8086 8.00842 11.8252 7.85842C10.8336 7.70842 10.2919 6.39175 10.6002 4.91675C10.9002 3.43342 11.9419 2.35842 12.9169 2.50008ZM2.50025 6.33342C3.45025 5.92508 4.74191 6.66675 5.41691 7.95842C6.05025 9.27508 5.83358 10.6584 4.89191 11.0668C3.95025 11.4751 2.66691 10.7418 2.00858 9.43342C1.35025 8.12508 1.58358 6.73342 2.50025 6.33342ZM17.5002 6.33342C18.4169 6.73342 18.6502 8.12508 17.9919 9.43342C17.3336 10.7418 16.0502 11.4751 15.1086 11.0668C14.1669 10.6584 13.9502 9.27508 14.5836 7.95842C15.2586 6.66675 16.5502 5.92508 17.5002 6.33342ZM16.1086 15.3168C16.1419 16.1001 15.5419 16.9668 14.8252 17.2918C13.3336 17.9751 11.5669 16.5584 9.90858 16.5584C8.25025 16.5584 6.46691 18.0334 5.00025 17.2918C4.16691 16.8834 3.59191 15.8001 3.70025 14.9001C3.85025 13.6584 5.34191 12.9918 6.22525 12.0834C7.40025 10.9084 8.23358 8.70008 9.90858 8.70008C11.5752 8.70008 12.4586 10.8751 13.5836 12.0834C14.5086 13.1001 16.0502 13.9584 16.1086 15.3168Z"
              fill={color}
            />
          </svg>
        );
      case "marketplace":
        return (
          <svg
            width="28"
            height="28"
            viewBox="0 0 20 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M10 10.8333C8.89493 10.8333 7.83512 10.3943 7.05372 9.61286C6.27232 8.83146 5.83333 7.77165 5.83333 6.66658H7.5C7.5 7.32963 7.76339 7.96551 8.23223 8.43435C8.70107 8.90319 9.33696 9.16658 10 9.16658C10.663 9.16658 11.2989 8.90319 11.7678 8.43435C12.2366 7.96551 12.5 7.32963 12.5 6.66658H14.1667C14.1667 7.77165 13.7277 8.83146 12.9463 9.61286C12.1649 10.3943 11.1051 10.8333 10 10.8333ZM10 2.49992C10.663 2.49992 11.2989 2.76331 11.7678 3.23215C12.2366 3.70099 12.5 4.33688 12.5 4.99992H7.5C7.5 4.33688 7.76339 3.70099 8.23223 3.23215C8.70107 2.76331 9.33696 2.49992 10 2.49992ZM15.8333 4.99992H14.1667C14.1667 4.45274 14.0589 3.91093 13.8495 3.4054C13.6401 2.89988 13.3332 2.44055 12.9463 2.05364C12.5594 1.66673 12.1 1.35982 11.5945 1.15042C11.089 0.941026 10.5472 0.833252 10 0.833252C8.89493 0.833252 7.83512 1.27224 7.05372 2.05364C6.27232 2.83504 5.83333 3.89485 5.83333 4.99992H4.16667C3.24167 4.99992 2.5 5.74159 2.5 6.66658V16.6666C2.5 17.1086 2.67559 17.5325 2.98816 17.8451C3.30072 18.1577 3.72464 18.3333 4.16667 18.3333H15.8333C16.2754 18.3333 16.6993 18.1577 17.0118 17.8451C17.3244 17.5325 17.5 17.1086 17.5 16.6666V6.66658C17.5 5.74159 16.75 4.99992 15.8333 4.99992Z"
              fill={color}
            />
          </svg>
        );
      case "medical-care":
        return (
          <svg
            width="28"
            height="28"
            viewBox="0 0 20 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M8.33445 2.5L6.66778 4.16667V5.83333H4.16778C3.20945 5.83333 2.60111 6.66667 2.50111 7.5L1.66778 15.8333C1.56778 16.6667 2.11778 17.5 3.33445 17.5H16.6678C17.8844 17.5 18.4344 16.6667 18.3344 15.8333L17.5011 7.5C17.4011 6.66667 16.7178 5.83333 15.8344 5.83333H13.3344V4.16667L11.6678 2.5H8.33445ZM8.33445 4.16667H11.6678V5.83333H8.33445V4.16667ZM9.16778 8.33333H10.8344V10.8333H13.3344V12.5H10.8344V15H9.16778V12.5H6.66778V10.8333H9.16778V8.33333Z"
              fill={color}
            />
          </svg>
        );
      case "appointments":
        return (
          <svg
            width="28"
            height="28"
            viewBox="0 0 16 18"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M5.5 8.33325V9.99992H3.83333V8.33325H5.5ZM8.83333 8.33325V9.99992H7.16667V8.33325H8.83333ZM12.1667 8.33325V9.99992H10.5V8.33325H12.1667ZM13.8333 2.49992C14.2754 2.49992 14.6993 2.67551 15.0118 2.98807C15.3244 3.30063 15.5 3.72456 15.5 4.16659V15.8333C15.5 16.2753 15.3244 16.6992 15.0118 17.0118C14.6993 17.3243 14.2754 17.4999 13.8333 17.4999H2.16667C1.24167 17.4999 0.5 16.7499 0.5 15.8333V4.16659C0.5 3.72456 0.675595 3.30063 0.988155 2.98807C1.30072 2.67551 1.72464 2.49992 2.16667 2.49992H3V0.833252H4.66667V2.49992H11.3333V0.833252H13V2.49992H13.8333ZM13.8333 15.8333V6.66658H2.16667V15.8333H13.8333ZM5.5 11.6666V13.3333H3.83333V11.6666H5.5ZM8.83333 11.6666V13.3333H7.16667V11.6666H8.83333ZM12.1667 11.6666V13.3333H10.5V11.6666H12.1667Z"
              fill={color}
            />
          </svg>
        );
      case "account-preferences":
        return (
          <svg
            width="28"
            height="28"
            viewBox="0 0 19 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M7.33464 0.333252C6.45058 0.333252 5.60273 0.684441 4.97761 1.30956C4.35249 1.93468 4.0013 2.78253 4.0013 3.66659C4.0013 4.55064 4.35249 5.39849 4.97761 6.02361C5.60273 6.64873 6.45058 6.99992 7.33464 6.99992C8.21869 6.99992 9.06654 6.64873 9.69166 6.02361C10.3168 5.39849 10.668 4.55064 10.668 3.66659C10.668 2.78253 10.3168 1.93468 9.69166 1.30956C9.06654 0.684441 8.21869 0.333252 7.33464 0.333252ZM13.168 6.99992C13.0596 6.99992 12.968 7.07492 12.9513 7.17492L12.793 8.27492C12.543 8.38325 12.3013 8.51659 12.0846 8.66658L11.0513 8.24992C10.9596 8.24992 10.8513 8.24992 10.793 8.35825L9.95963 9.79992C9.90963 9.89158 9.9263 9.99992 10.0096 10.0666L10.893 10.7499C10.8763 10.8916 10.868 11.0249 10.868 11.1666C10.868 11.3083 10.8763 11.4416 10.893 11.5833L10.0096 12.2666C9.93464 12.3333 9.90963 12.4416 9.95963 12.5333L10.793 13.9749C10.843 14.0833 10.9513 14.0833 11.0513 14.0833L12.0846 13.6666C12.3013 13.8166 12.5346 13.9583 12.793 14.0583L12.9513 15.1583C12.968 15.2583 13.0513 15.3333 13.168 15.3333H14.8346C14.9263 15.3333 15.018 15.2583 15.0346 15.1583L15.193 14.0583C15.443 13.9499 15.668 13.8166 15.893 13.6666L16.918 14.0833C17.0263 14.0833 17.1346 14.0833 17.193 13.9749L18.0263 12.5333C18.0763 12.4416 18.0513 12.3333 17.9763 12.2666L17.0846 11.5833C17.1013 11.4416 17.118 11.3083 17.118 11.1666C17.118 11.0249 17.1096 10.8916 17.0846 10.7499L17.968 10.0666C18.043 9.99992 18.068 9.89158 18.018 9.79992L17.1846 8.35825C17.1346 8.24992 17.0263 8.24992 16.918 8.24992L15.893 8.66658C15.668 8.51659 15.443 8.37492 15.1846 8.27492L15.0263 7.17492C15.018 7.07492 14.9263 6.99992 14.8346 6.99992H13.168ZM7.33464 8.66658C3.6513 8.66658 0.667969 10.1583 0.667969 11.9999V13.6666H8.73464C8.36272 12.8856 8.16915 12.0316 8.16797 11.1666C8.16966 10.3293 8.35156 9.50228 8.7013 8.74158C8.25964 8.69159 7.8013 8.66658 7.33464 8.66658ZM14.0013 9.91658C14.693 9.91658 15.2513 10.4749 15.2513 11.1666C15.2513 11.8583 14.693 12.4166 14.0013 12.4166C13.3013 12.4166 12.7513 11.8583 12.7513 11.1666C12.7513 10.4749 13.3096 9.91658 14.0013 9.91658Z"
              fill={color}
            />
          </svg>
        );
      case "notification":
        return (
          <svg width="28" height="28" viewBox="0 0 24 24" fill={color}>
            <path d="M12 22c1.1 0 2-.9 2-2h-4c0 1.1.89 2 2 2zm6-6v-5c0-3.07-1.64-5.64-4.5-6.32V4c0-.83-.67-1.5-1.5-1.5s-1.5.67-1.5 1.5v.68C7.63 5.36 6 7.92 6 11v5l-2 2v1h16v-1l-2-2z" />
          </svg>
        );
      // Shopping cart icon - Commented out for now - will be used later
      // case "shopping-cart":
      //   return (
      //     <svg width="24" height="24" viewBox="0 0 24 24" fill={color}>
      //       <path d="M7 18c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zM1 2v2h2l3.6 7.59-1.35 2.45c-.16.28-.25.61-.25.96 0 1.1.9 2 2 2h12v-2H7.42c-.14 0-.25-.11-.25-.25l.03-.12L8.1 13h7.45c.75 0 1.41-.41 1.75-1.03L21.7 4H5.21l-.94-2H1zm16 16c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z" />
      //     </svg>
      //   );
      case "favorites":
        return (
          <svg width="24" height="24" viewBox="0 0 24 24" fill={color}>
            <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
          </svg>
        );
      case "search":
        return (
          <svg width="24" height="24" viewBox="0 0 24 24" fill={color}>
            <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z" />
          </svg>
        );
      case "chat":
        return (
          <svg width="28" height="28" viewBox="0 0 24 24" fill={color}>
            <path d="M20 2H4c-1.1 0-1.99.9-1.99 2L2 22l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-2 12H6v-2h12v2zm0-3H6V9h12v2zm0-3H6V6h12v2z" />
          </svg>
        );
      case "ai-chat":
        return (
          <svg width="28" height="28" viewBox="0 0 24 24" fill={color}>
            <path d="M20 2H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h4l4 4 4-4h4c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2z" />
          </svg>
        );
      case "settings-gear":
        return (
          <svg width="16" height="16" viewBox="0 0 24 24" fill={color}>
            <path d="M19.43 12.98c.04-.32.07-.64.07-.98s-.03-.66-.07-.98l2.11-1.65c.19-.15.24-.42.12-.64l-2-3.46c-.12-.22-.39-.3-.61-.22l-2.49 1c-.52-.4-1.09-.74-1.71-.98L12.75 3.03c-.05-.23-.25-.4-.49-.4H9.75c-.24 0-.44.17-.49.4l-.42 2.41c-.62.24-1.19.58-1.71.98l-2.49-1c-.22-.09-.49 0-.61.22l-2 3.46c-.12.22-.07.49.12.64l2.11 1.65c-.04.32-.07.64-.07.98s.03.66.07.98l-2.11 1.65c-.19.15-.24.42-.12.64l2 3.46c.12.22.39.3.61.22l2.49-1c.52.4 1.09.74 1.71.98l.42 2.41c.05.23.25.4.49.4h2.5c.24 0 .44-.17.49-.4l.42-2.41c.62-.24 1.19-.58 1.71-.98l2.49 1c.22.09.49 0 .61-.22l2-3.46c.12-.22.07-.49-.12-.64l-2.11-1.65zM12 15.5c-1.93 0-3.5-1.57-3.5-3.5s1.57-3.5 3.5-3.5 3.5 1.57 3.5 3.5-1.57 3.5-3.5 3.5z" />
          </svg>
        );
      case "support":
        return (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M3 11h3a2 2 0 0 1 2 2v3a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-5Zm0 0a9 9 0 1 1 18 0m0 0v5a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3Z" />
            <path d="M21 16v2a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v2" style={{ display: 'none' }} />
          </svg>
        );
      case "log-out":
        return (
          <svg width="16" height="16" viewBox="0 0 24 24" fill={color}>
            <path d="M17 7l-1.41 1.41L18.17 11H8v2h10.17l-2.58 2.58L17 17l5-5zM4 5h8V3H4c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h8v-2H4V5z" />
          </svg>
        );
      case "chevron-down":
        return (
          <svg width="20" height="24" viewBox="0 0 24 24" fill={color}>
            <path d="M7 10l5 5 5-5z" />
          </svg>
        );
      case "group":
        return (
          <svg width="28" height="28" viewBox="0 0 24 24" fill={color}>
            <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z" />
          </svg>
        );
      case "adoption":
        // Icon source: /icons/navbar/adoption.svg
        // This SVG is loaded inline to support dynamic color changes (hover/active states)
        return (
          <svg
            width="28"
            height="28"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            {/* House outline */}
            <path
              d="M4 10.5L12 4l8 6.5V19a2 2 0 0 1-2 2h-4.5v-4.25h-3V21H6A2 2 0 0 1 4 19v-8.5Z"
              stroke={color}
              strokeWidth="1.9"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            {/* Heart + paw inside the house */}
            <path
              d="M12 10.75c-.9-1.1-2.6-1.2-3.6-.1-.9.9-.9 2.4 0 3.3L12 17l3.6-3.05c.9-.9.9-2.4 0-3.3-1-1.1-2.7-1-3.6.1Z"
              fill={color}
              fillOpacity="0.9"
            />
            <circle cx="10" cy="11" r="0.6" fill="#FFFFFF" />
            <circle cx="14" cy="11" r="0.6" fill="#FFFFFF" />
            <circle cx="12" cy="12.7" r="0.7" fill="#FFFFFF" />
          </svg>
        );
      default:
        return (
          <svg width="24" height="24" viewBox="0 0 24 24" fill={color}>
            <circle cx="12" cy="12" r="10" />
          </svg>
        );
    }
  };

  return getIconSvg(iconName, iconColor);
};

// Navbar Component
// Using React.memo to prevent unnecessary re-renders
const Navbar = React.memo(({ userType: propUserType }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  // All state hooks first
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isNotificationOpen, setIsNotificationOpen] = useState(false);
  const [isChatDropdownOpen, setIsChatDropdownOpen] = useState(false);
  const [isSupportModalOpen, setIsSupportModalOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isMediumScreen, setIsMediumScreen] = useState(false);
  const [isUltraSmallScreen, setIsUltraSmallScreen] = useState(false);
  const [highlightNotificationId, setHighlightNotificationId] = useState(null);

  // Check for webview header preference
  const queryParams = new URLSearchParams(location.search);
  const isHeaderParam = queryParams.get("isHeader");

  // Persist isHeader preference to sessionStorage
  useEffect(() => {
    if (isHeaderParam !== null) {
      sessionStorage.setItem("webview_is_header", isHeaderParam);
    }
  }, [isHeaderParam]);

  const storedIsHeader = sessionStorage.getItem("webview_is_header");
  const shouldHideHeader = (isHeaderParam === "false" || storedIsHeader === "false") && isMobile;
  useEffect(() => {
    const checkScreenSize = () => {
      const width = window.innerWidth;
      setIsMobile(width <= 768);
      // Medium screen: 769px to 1024px (inclusive)
      setIsMediumScreen(width >= 769 && width <= 1024);
      setIsUltraSmallScreen(width <= 425);
    };

    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isSearchModalOpen, setIsSearchModalOpen] = useState(false);
  const [isLogoutModalOpen, setIsLogoutModalOpen] = useState(false);
  const [pendingFollowRequestsCount, setPendingFollowRequestsCount] =
    useState(0);

  // Ref to track if we've already reset notification count for this dropdown open
  const hasResetNotificationCountRef = useRef(false);
  const [isFetchingNotifications, setIsFetchingNotifications] = useState(false);
  const [hoveredNavItemIndex, setHoveredNavItemIndex] = useState(null);
  const [isSearchHovered, setIsSearchHovered] = useState(false);
  const [isNotificationIconHovered, setIsNotificationIconHovered] =
    useState(false);
  const [isChatIconHovered, setIsChatIconHovered] = useState(false);
  // Initialize AI Chat state from localStorage
  const [isAIChatOpen, setIsAIChatOpen] = useState(() => {
    const stored = localStorage.getItem("ai_chat_open");
    return stored === "true";
  });
  const [isAIChatMinimized, setIsAIChatMinimized] = useState(() => {
    const stored = localStorage.getItem("ai_chat_minimized");
    return stored === "true";
  });
  const [isAIChatIconHovered, setIsAIChatIconHovered] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showSearchDropdown, setShowSearchDropdown] = useState(false);
  const [searchBarPosition, setSearchBarPosition] = useState({ top: 100, left: 0, width: 600 });
  const [isMobileSearchOpen, setIsMobileSearchOpen] = useState(false);
  const [isMobileSearchIconHovered, setIsMobileSearchIconHovered] = useState(false);
  const [isDesktopSearchVisible, setIsDesktopSearchVisible] = useState(true);
  // Firm access-denied toast — must be declared here (before any early returns)
  const [firmToast, setFirmToast] = useState(null);
  // const [isCartIconHovered, setIsCartIconHovered] = useState(false); // Commented out for now - will be used later

  // All refs
  const redirectedRef = useRef(false);
  const mountedRef = useRef(true);
  const mobileMenuRef = useRef(null);
  const searchInputRef = useRef(null);
  const searchDropdownRef = useRef(null);
  const mobileSearchDropdownRef = useRef(null);
  const searchBarContainerRef = useRef(null);

  // Hamburger menu handler with robust double-trigger prevention
  const handleHamburgerClick = useHamburgerMenu(
    () => setIsMobileMenuOpen((open) => !open)
  );

  // UNIFIED: Use single authentication hook
  const {
    user,
    isAuthenticated,
    isLoading,
    userCounts,
    getUserType,
    getDisplayName,
    getProfileImageUrl,
    getInitials,
    logout,
    updateNotificationCount,
  } = useAuth();

  // All useEffect hooks
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Persist AI Chat open state to localStorage
  useEffect(() => {
    localStorage.setItem("ai_chat_open", isAIChatOpen.toString());
  }, [isAIChatOpen]);

  // Persist AI Chat minimized state to localStorage
  useEffect(() => {
    localStorage.setItem("ai_chat_minimized", isAIChatMinimized.toString());
  }, [isAIChatMinimized]);

  // Minimize chat when navigating to a new page
  useEffect(() => {
    // If chat is open and not minimized, minimize it on navigation
    if (isAIChatOpen && !isAIChatMinimized) {
      setIsAIChatMinimized(true);
    }
  }, [location.pathname]); // Only depend on pathname to avoid unnecessary re-renders

  // Listen for chat badge updates
  useEffect(() => {
    const handleChatBadgeUpdate = (event) => {
      const { count } = event.detail;

      // Update the local state to trigger re-render
      // This will update the badge display
      if (mountedRef.current) {
        // Force a re-render by updating a state variable
        setHoveredNavItemIndex((prev) => (prev === null ? 0 : null));
      }
    };

    // Listen for chat badge update events
    window.addEventListener("chat-badge-update", handleChatBadgeUpdate);

    // Initialize chat badge count from localStorage
    const storedChatCount = localStorage.getItem("chat_badge_count");
    if (storedChatCount) {
      setPendingFollowRequestsCount(parseInt(storedChatCount, 10));
    }

    return () => {
      window.removeEventListener("chat-badge-update", handleChatBadgeUpdate);
    };
  }, []);

  // Listen for notification badge updates
  useEffect(() => {
    const handleNotificationBadgeUpdate = (event) => {
      const { count } = event.detail;

      // Update notification count using the updateNotificationCount function
      if (mountedRef.current && updateNotificationCount) {
        // Get current count and increment it
        const currentCount = userCounts?.notifications || 0;
        const newCount = currentCount + 1;
        updateNotificationCount(newCount);
      }
    };

    // Listen for notification badge update events
    window.addEventListener("notification-badge-update", handleNotificationBadgeUpdate);

    return () => {
      window.removeEventListener("notification-badge-update", handleNotificationBadgeUpdate);
    };
  }, [updateNotificationCount, userCounts?.notifications]);

  // Function to reset chat badge count
  const resetChatBadgeCount = () => {
    localStorage.removeItem("chat_badge_count");
  };

  useEffect(() => {
    if (redirectedRef.current || !mountedRef.current) {
      return;
    }

    if (!isLoading && !isAuthenticated) {
      redirectedRef.current = true;
      localStorage.setItem("intended_route", location.pathname);
      navigate("/auth/login", { replace: true });
    }
  }, [isLoading, isAuthenticated, navigate, location.pathname]);

  // Reset notification badge count when dropdown opens
  useEffect(() => {
    if (isNotificationOpen && updateNotificationCount) {
      // Only reset once per dropdown open session
      if (!hasResetNotificationCountRef.current) {
        const currentCount = userCounts?.notifications || 0;
        if (currentCount > 0) {
          updateNotificationCount(0);
          hasResetNotificationCountRef.current = true;
        }
      }
    } else {
      // Reset the flag when dropdown closes
      hasResetNotificationCountRef.current = false;
      // Clear highlight when dropdown closes
      setHighlightNotificationId(null);
    }
    // Only depend on isNotificationOpen and updateNotificationCount
    // Don't include userCounts?.notifications to prevent infinite loops
  }, [isNotificationOpen, updateNotificationCount]);

  // Listen for open-notification-dropdown event from toasts
  useEffect(() => {
    const handleOpenNotificationDropdown = (event) => {
      const { notificationId } = event.detail;

      // Set highlight notification ID and open dropdown
      setHighlightNotificationId(notificationId);
      setIsNotificationOpen(true);
    };

    window.addEventListener('open-notification-dropdown', handleOpenNotificationDropdown);

    return () => {
      window.removeEventListener('open-notification-dropdown', handleOpenNotificationDropdown);
    };
  }, []);

  // useEffect(() => {
  //   fetchPendingFollowRequestsCount()

  //   // const interval = setInterval(() => {
  //   //   fetchPendingFollowRequestsCount()
  //   // }, 600000)

  //   // return () => clearInterval(interval)
  // }, [fetchPendingFollowRequestsCount])

  // Firm access-toast listener — must be here (before any early returns)
  useEffect(() => {
    const handleFirmToast = (event) => {
      const { message } = event.detail || {};
      setFirmToast({
        message: message || 'This page is not available for Firm accounts.',
      });
      // Auto-dismiss after 4 seconds
      const timer = setTimeout(() => setFirmToast(null), 4000);
      return () => clearTimeout(timer);
    };
    window.addEventListener('show-access-toast', handleFirmToast);
    return () => window.removeEventListener('show-access-toast', handleFirmToast);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      // Skip if this is a touch event that might have a click event following
      if (event.type === 'touchstart' || event.type === 'touchend') {
        return;
      }

      if (
        isNotificationOpen &&
        !event.target.closest(".notification-container")
      ) {
        setIsNotificationOpen(false);
      }

      if (isProfileOpen && !event.target.closest(".profile-container")) {
        setIsProfileOpen(false);
      }

      // Mobile menu close on outside click - be more specific about the button
      const menuButton = event.target.closest(".navbar-menu-btn");
      const menuContainer = event.target.closest(".mobile-menu-container");

      if (
        isMobileMenuOpen &&
        !menuButton &&
        !menuContainer &&
        mobileMenuRef.current &&
        !mobileMenuRef.current.contains(event.target)
      ) {
        setIsMobileMenuOpen(false);
      }
    };

    // Use passive listeners and add some delay to prevent conflicts
    document.addEventListener("mousedown", handleClickOutside, { passive: true });
    document.addEventListener("touchstart", handleClickOutside, { passive: true });

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, [isNotificationOpen, isProfileOpen, isMobileMenuOpen]);

  // Client-side filtering function for search results
  const filterSearchResults = (results, query) => {
    if (!query || !query.trim()) {
      return results;
    }
    const searchLower = query.trim().toLowerCase();
    return results.filter((user) => {
      // Handle both API response formats: user_name/full_name and username/first_name/last_name
      const firstName = (user.first_name || "").toLowerCase();
      const lastName = (user.last_name || "").toLowerCase();
      const username = (user.username || user.user_name || "").toLowerCase();
      const fullName = (user.full_name || "").toLowerCase();

      // Check if search matches anywhere in first name, last name, username, or full name
      return (
        firstName.includes(searchLower) ||
        lastName.includes(searchLower) ||
        username.includes(searchLower) ||
        fullName.includes(searchLower)
      );
    });
  };

  // Handle search functionality - must be before conditional returns
  const handleSearch = async (query) => {
    if (!query.trim()) {
      setSearchResults([]);
      setShowSearchDropdown(false);
      return;
    }

    setIsSearching(true);
    setShowSearchDropdown(true);

    try {
      const response = await userAPI.searchUsers({
        query: query.trim()
      });

      if (response.data.status === "success") {
        // Filter out ADMIN and CLINIC_MODERATOR users from search results
        // CLINIC_MODERATOR users should only appear in clinic team management
        const filteredResults = (response.data.data || []).filter(
          user =>
            user.user_type !== 'ADMIN' &&
            user.user_type !== 'admin' &&
            user.user_type !== 'CLINIC_MODERATOR' &&
            user.user_type !== 'clinic_moderator'
        );
        setSearchResults(filteredResults);
      } else {
        setSearchResults([]);
      }
    } catch (error) {
      console.error("Search error:", error);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  // Debounce search - must be before conditional returns
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchQuery) {
        handleSearch(searchQuery);
      } else {
        setSearchResults([]);
        setShowSearchDropdown(false);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery]);

  // Check if desktop search is visible (not hidden by CSS)
  useEffect(() => {
    const checkDesktopSearchVisibility = () => {
      if (searchBarContainerRef.current) {
        const parent = searchBarContainerRef.current.closest('.navbar-desktop-items');
        if (parent) {
          const style = window.getComputedStyle(parent);
          const isVisible = style.display !== 'none' &&
            style.visibility !== 'hidden' &&
            parent.offsetWidth > 0 &&
            parent.offsetHeight > 0;
          setIsDesktopSearchVisible(isVisible);
        } else {
          setIsDesktopSearchVisible(false);
        }
      } else {
        // If no desktop search container, assume it's hidden (mobile view)
        setIsDesktopSearchVisible(false);
      }
    };

    checkDesktopSearchVisibility();
    window.addEventListener("resize", checkDesktopSearchVisibility);

    // Also check on initial load after a short delay
    const timeoutId = setTimeout(checkDesktopSearchVisibility, 100);

    // Check when mobile search opens/closes
    const checkInterval = setInterval(checkDesktopSearchVisibility, 200);

    return () => {
      window.removeEventListener("resize", checkDesktopSearchVisibility);
      clearTimeout(timeoutId);
      clearInterval(checkInterval);
    };
  }, [isMobileSearchOpen]);

  // Update search bar position when dropdown is shown - must be before conditional returns
  useEffect(() => {
    if (showSearchDropdown && searchBarContainerRef.current && isDesktopSearchVisible) {
      const updatePosition = () => {
        if (searchBarContainerRef.current) {
          const rect = searchBarContainerRef.current.getBoundingClientRect();
          setSearchBarPosition({
            top: rect.bottom, // Use viewport-relative position for fixed positioning
            left: rect.left, // Use viewport-relative position for fixed positioning
            width: rect.width,
            height: rect.height,
          });
        }
      };

      updatePosition();
      window.addEventListener("scroll", updatePosition, true);
      window.addEventListener("resize", updatePosition);

      return () => {
        window.removeEventListener("scroll", updatePosition, true);
        window.removeEventListener("resize", updatePosition);
      };
    }
  }, [showSearchDropdown, isDesktopSearchVisible]);

  // Close dropdown when clicking outside - must be before conditional returns
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check desktop search dropdown
      if (
        isDesktopSearchVisible &&
        !isMobileSearchOpen &&
        searchDropdownRef.current &&
        !searchDropdownRef.current.contains(event.target) &&
        searchInputRef.current &&
        !searchInputRef.current.contains(event.target) &&
        !event.target.closest('.mobile-search-container')
      ) {
        setShowSearchDropdown(false);
      }

      // Check mobile search dropdown
      if (
        isMobileSearchOpen &&
        mobileSearchDropdownRef.current &&
        !mobileSearchDropdownRef.current.contains(event.target) &&
        !event.target.closest('.mobile-search-container') &&
        !event.target.closest('.mobile-search-input-container')
      ) {
        setShowSearchDropdown(false);
      }

      // Close mobile search if clicking outside
      if (
        isMobileSearchOpen &&
        !event.target.closest('.mobile-search-container') &&
        !event.target.closest('.mobile-search-input-container')
      ) {
        setIsMobileSearchOpen(false);
      }
    };

    if (showSearchDropdown || isMobileSearchOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [showSearchDropdown, isMobileSearchOpen, isDesktopSearchVisible]);

  // Now handle conditional returns after all hooks
  if (isLoading) {
    return (
      <nav
        style={{
          width: "100%",
          minHeight: "80px",
          background: "#FFFFFF",
          boxShadow: "0 2px 8px rgba(0, 0, 0, 0.04)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "16px 12px",
          position: "fixed",
          top: 0,
          left: 0,
          zIndex: 1000,
          borderBottom: "1px solid #F0F0F0",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            color: "#666",
            fontFamily: '"Red Hat Display", sans-serif',
          }}
        >
          <div
            style={{
              width: "20px",
              height: "20px",
              border: "2px solid #f3f3f3",
              borderTop: "2px solid #8548A8",
              borderRadius: "50%",
              animation: "spin 1s linear infinite",
            }}
          ></div>
          Loading...
        </div>
        <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
      </nav>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  if (!getUserType || !getDisplayName) {
    console.warn("Navbar: Required auth functions not available");
    return null;
  }

  const userType = getUserType();

  if (shouldHideHeader) {
    return null;
  }

  // Navigation function
  const handleNavigation = (path) => {
    // If already on the target path (especially /social), scroll to top
    if (path === "/social" && location.pathname === "/social") {
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    // Reset chat badge count when visiting chat page
    if (path === "/chat") {
      resetChatBadgeCount();
    }

    try {
      navigate(path);
    } catch (error) {
      console.error("Navigation failed:", error);
    }
  };

  const handleLogoutClick = () => {
    setIsProfileOpen(false);
    setIsLogoutModalOpen(true);
  };

  const handleLogout = async () => {
    try {
      setIsProfileOpen(false);
      setIsLogoutModalOpen(false);


      // Clear all session data and cookies
      const clearAllSessionData = () => {
        // Clear localStorage
        localStorage.clear();

        // Clear sessionStorage
        sessionStorage.clear();

        // Clear all cookies
        document.cookie.split(";").forEach(function (c) {
          document.cookie = c
            .replace(/^ +/, "")
            .replace(
              /=.*/,
              "=;expires=" + new Date().toUTCString() + ";path=/"
            );
        });

        // Clear specific auth-related cookies
        const cookiesToClear = [
          "access_token",
          "refresh_token",
          "user_data",
          "device_id",
          "last_login",
          "demo_mode",
          "intended_route",
          "lang",
          "theme",
          "preferences",
        ];

        cookiesToClear.forEach((cookieName) => {
          document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
          document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${window.location.hostname}`;
        });

      };

      // Clear session data first
      clearAllSessionData();


      // Attempt API logout
      const success = await logout();

      if (success) {
      } else {
      }

      // Mark as redirected to prevent loops
      redirectedRef.current = true;

      // Force navigation with replace to prevent back button issues
      window.location.replace("/auth/login");
    } catch (error) {
      console.error("Navbar: Logout error:", error);

      // Clear session data even if API call fails
      localStorage.clear();
      sessionStorage.clear();
      document.cookie.split(";").forEach(function (c) {
        document.cookie = c
          .replace(/^ +/, "")
          .replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/");
      });


      // Ensure redirection even if error occurs
      redirectedRef.current = true;
      window.location.replace("/auth/login");
    }
  };

  const handleNotificationClick = () => {
    if (isMobile) {
      // Navigate to notifications page on mobile
      const userTypeRoute = userType?.toLowerCase()?.replace(/_/g, '-') || 'pet-guardian';
      navigate(`/dashboard/${userTypeRoute}/notifications`);
    } else {
      // Show dropdown on desktop
      const willBeOpen = !isNotificationOpen;
      setIsChatDropdownOpen(false);
      setIsNotificationOpen(willBeOpen);

    }
  };

  const handleAIChatToggle = () => {
    const willBeOpen = !isAIChatOpen;
    setIsAIChatOpen(willBeOpen);
    if (willBeOpen && isAIChatMinimized) {
      setIsAIChatMinimized(false);
    }
  };

  // Safe display name and profile handling
  const displayName = getDisplayName() || "User";
  const profileImageUrl = getProfileImageUrl();
  const userInitials = getInitials() || "U";
  const userEmail = user?.email || t("navbar.No_Email");

  // Determine user type with fallback
  const getColorScheme = (userType) => {
    const type = userType?.toUpperCase();
    switch (type) {
      case "PET_GUARDIAN":
        return {
          primary: "#8548A8",
          gradient: "linear-gradient(135deg, #8548A8 0%, #B968C7 100%)",
          hover: "#7A4397",
          profileBg: "#B968C7",
          secondary: "#F3E8FF",
          accent: "#7A4397",
          light: "#FAF5FF",
          badge: "#E9D5FF",
        };
      case "VETERINARIAN":
      case "VET":
        return {
          primary: "#00D4B3",
          gradient: "linear-gradient(135deg, #00D4B3 0%, #1DE9B6 100%)",
          hover: "#00C4A7",
          profileBg: "#1DE9B6",
          secondary: "#E0FBDE",
          accent: "#00C4A7",
          light: "#F0FFF4",
          badge: "#C6F6D5",
        };
      case "SERVICE_PROVIDER":
      case "BUSINESS":
        return {
          primary: "#D65745",
          gradient: "linear-gradient(135deg, #D65745 0%, #FF6B6B 100%)",
          hover: "#C7493A",
          profileBg: "#FF6B6B",
          secondary: "#FEEBE7",
          accent: "#C7493A",
          light: "#FFF5F5",
          badge: "#FED7D7",
        };
      case "FIRM":
        return {
          primary: "#1B3A75",
          gradient: "linear-gradient(135deg, #1B3A75 0%, #2D5BBF 100%)",
          hover: "#162E5E",
          profileBg: "#2D5BBF",
          secondary: "#E8EEF8",
          accent: "#162E5E",
          light: "#F0F4FC",
          badge: "#C7D5ED",
        };
      default:
        return {
          primary: "#8548A8",
          gradient: "linear-gradient(135deg, #8548A8 0%, #B968C7 100%)",
          hover: "#7A4397",
          profileBg: "#B968C7",
          secondary: "#F3E8FF",
          accent: "#7A4397",
          light: "#FAF5FF",
          badge: "#E9D5FF",
        };
    }
  };

  const colors = getColorScheme(userType);
  const userId = user?.id || user?.user_id;

  // ─────────────────────────────────────────────────────────────────────────

  // Helper: translation with safe fallback (avoids showing raw keys like "navbar.Adoption")
  const trNav = (key, fallback) => {
    const value = t(key);
    return value === key ? (fallback || key) : value;
  };

  const navigationItems = [
    {
      icon: "social-media",
      label: trNav("navbar.Sulhafa_Connect", "Sulhafa Connect"),
      path: `/social`,
    },
    {
      icon: "adoption",
      label: trNav("navbar.Adoption", "Adoption"),
      path: `/adoption`,
    },
    // Medical Care — hidden for Firm accounts (Care page is restricted)
    ...(userType !== USER_TYPES.FIRM ? [{
      icon: "medical-care",
      label: t("navbar.Medical_Care"),
      path: userType === "PET_GUARDIAN"
        ? `/medical/dashboard/petguardian`
        : userType === "SERVICE_PROVIDER"
          ? `/medical/dashboard/petshopowner`
          : userType === "VETERINARIAN"
            ? `/medical/clinic/redirect-to-clinic`
            : `/medical/clinic/redirect-to-clinic`
    }] : []),
  ];

  return (
    <>
      {/* Firm access-denied toast */}
      {firmToast && (
        <div
          style={{
            position: 'fixed',
            bottom: '24px',
            left: '50%',
            transform: 'translateX(-50%)',
            background: '#1B3A75',
            color: '#fff',
            padding: '12px 24px',
            borderRadius: '10px',
            fontSize: '14px',
            fontFamily: '"Red Hat Display", sans-serif',
            fontWeight: 500,
            boxShadow: '0 4px 20px rgba(27,58,117,0.35)',
            zIndex: 99999,
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            animation: 'slideUpFirmToast 0.3s ease',
            maxWidth: '90vw',
          }}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="12" cy="12" r="10" stroke="#fff" strokeWidth="2"/>
            <path d="M12 8v4M12 16h.01" stroke="#fff" strokeWidth="2" strokeLinecap="round"/>
          </svg>
          {firmToast.message}
          <style>{`
            @keyframes slideUpFirmToast {
              from { opacity: 0; transform: translateX(-50%) translateY(12px); }
              to   { opacity: 1; transform: translateX(-50%) translateY(0); }
            }
          `}</style>
        </div>
      )}
      <nav
        className="navbar"
        style={{
          '--navbar-primary-color': colors.primary,
        }}
      >
        {/* Flex container for logo and nav items */}
        {/* Logo Section */}
        <div
          className="navbar-logo-container"
          onClick={() => {
            const ut = getUserType();
            if (ut === 'ADMIN' || ut === 'CONTENT_ADMIN') {
              return navigate('/admin/dashboard');
            }
            navigate("/social");
          }}
          style={{ cursor: "pointer" }}
        >
          <img
            src="/icons/navbar/Sulhafa_Beta_Logo.png"
            alt="Sulhafa Logo"
            className="navbar-logo"
            onError={(e) => {
              e.target.style.display = "none";
              e.target.nextSibling &&
                (e.target.nextSibling.style.display = "block");
            }}
          />
        </div>


        {/* Search Bar in Center */}
        <div
          className="navbar-desktop-items"
          style={{
            display: "flex",
            alignItems: "center",
            gap: "clamp(8px, 2vw, 24px)",
            flex: 1,
            justifyContent: "center",
            maxWidth: "800px",
            margin: "0 auto",
            overflow: "visible",
            visibility: "visible",
            position: "relative",
            zIndex: 10002,
          }}
        >
          <div
            ref={searchBarContainerRef}
            className="navbar-search-container"
            onMouseEnter={(e) => {
              setIsSearchHovered(true);
            }}
            onMouseLeave={(e) => {
              setIsSearchHovered(false);
            }}
          >
            <IconComponent
              iconName="search"
              isHovered={isSearchHovered}
              colors={colors}
              defaultColor="rgba(73, 73, 73, 0.6)"
            />
            <input
              ref={searchInputRef}
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onFocus={(e) => {
                setIsSearchHovered(true);
                if (searchQuery || searchResults.length > 0) {
                  setShowSearchDropdown(true);
                }
              }}
              onBlur={() => {
                setIsSearchHovered(false);
              }}
              placeholder={t("navbar.SearchUser") || "Search for Users..."}
              className="navbar-search-input"
            />
            {isSearching && (
              <div
                className="navbar-search-spinner"
                style={{
                  borderTopColor: colors.primary,
                }}
              />
            )}
          </div>

          {/* Search Results Dropdown - Only show for desktop search when not using mobile search */}
          {typeof document !== 'undefined' && isDesktopSearchVisible && !isMobileSearchOpen && showSearchDropdown && (searchResults.length > 0 || isSearching || searchQuery) &&
            createPortal(
              <div
                ref={searchDropdownRef}
                className="navbar-search-dropdown"
                style={{
                  top: `${(searchBarPosition?.top || 100) + 8}px`, // 8px gap below search bar
                  left: `${searchBarPosition?.left || 0}px`,
                  width: `${searchBarPosition?.width || 600}px`,
                  transform: 'none', // Ensure no transform is applied that could center it
                }}
              >
                {isSearching && searchResults.length === 0 ? (
                  <div className="navbar-search-empty">
                    Searching...
                  </div>
                ) : filterSearchResults(searchResults, searchQuery).length > 0 ? (
                  filterSearchResults(searchResults, searchQuery).map((result) => (
                    <div
                      key={result.user_id || result.id}
                      onClick={() => {
                        navigate(`/social/profile/${result.user_id || result.id}`);
                        setShowSearchDropdown(false);
                        setSearchQuery("");
                      }}
                      className="navbar-search-result-item"
                    >
                      <img
                        src={result.profile_image_url || result.image || getUserPlaceholderImage(result.gender)}
                        alt={`${result.first_name || ''} ${result.last_name || ''}`.trim() || result.name || result.username}
                        className="navbar-search-result-image"
                        style={{
                          borderColor: getColorScheme(result.user_type).primary,
                        }}
                        onError={(e) => {
                          e.target.src = getUserPlaceholderImage(result.gender);
                        }}
                      />
                      <div style={{ flex: 1 }}>
                        <div className="navbar-search-result-name">
                          {result.full_name || `${result.first_name || ''} ${result.last_name || ''}`.trim() || result.name || result.user_name || result.username}
                        </div>
                      </div>
                    </div>
                  ))
                ) : searchQuery && !isSearching ? (
                  <div className="navbar-search-empty">
                    No results found
                  </div>
                ) : null}
              </div>,
              document.body
            )}
        </div>



        {/* Right Side Icons - Language Toggle, Sulhafa Connect, Medical Care, Notifications, Chat, Profile, Hamburger */}
        {/* Cart icon commented out for now - will be used later */}
        <div
          className="navbar-right-icons"
          style={{
            display: "flex",
            alignItems: "center",
            gap: "12px", // Consistent gap between all icons
            flexShrink: 0, // Prevent icons from shrinking
          }}
        >
          {/* Language Toggle */}
          {/* Sulhafa Connect and Medical Care - Moved from center */}
          {/* Hide navigation items when hamburger button is visible (screens <= 1300px) */}
          {navigationItems.map((item, index) => {
            if (getUserType() == USER_TYPES.CLINIC_MODERATOR) {
              return null;
            }
            // For medical-care, check if we're in any clinic operations section
            let isActive = false;
            const pathname = location.pathname;

            // Explicitly check for social-media first and exclude medical paths
            if (item.icon === "social-media") {
              // Only active on exact /social path or paths starting with /social/
              // Explicitly exclude ALL medical paths - this must be false on any medical page
              const isOnSocialPage = pathname === "/social" || pathname.startsWith("/social/");
              const isOnMedicalPage = pathname.startsWith("/medical");
              isActive = isOnSocialPage && !isOnMedicalPage;
            } else if (item.icon === "medical-care") {
              // Active if on medical-care path OR any clinic operations route
              isActive = pathname.startsWith(item.path) ||
                pathname.startsWith("/medical/clinic/appointment-management") ||
                pathname.startsWith("/medical/clinic/chat") ||
                pathname.startsWith("/reception") ||
                (pathname.startsWith("/medical/clinic") &&
                  (pathname.includes("/appointment-management") ||
                    pathname.includes("/chat") ||
                    pathname.includes("/dashboard"))) ||
                pathname.startsWith("/medical/dashboard");
            } else {
              // Use startsWith for more precise matching instead of includes
              isActive = pathname === item.path || pathname.startsWith(item.path + "/");
            }
            const isHovered = hoveredNavItemIndex === index;

            return (
              <button
                key={index}
                onClick={() => handleNavigation(item.path)}
                onMouseEnter={() => setHoveredNavItemIndex(index)}
                onMouseLeave={() => setHoveredNavItemIndex(null)}
                className={`navbar-nav-item-btn ${isActive ? 'active' : ''} hide-on-mobile`}
                style={{
                  color: (isHovered || isActive) ? colors.primary : "rgba(73, 73, 73, 0.6)",
                }}
                title={item.label}
              >
                <IconComponent
                  iconName={item.icon}
                  isActive={isActive}
                  isHovered={isHovered}
                  colors={colors}
                />
              </button>
            );
          })}

          {/* Community Button - Only visible on medium screens (769px to 1024px) */}
          {isMediumScreen && (
            <button
              onClick={() => handleNavigation("/social/channels")}
              onMouseEnter={() => setHoveredNavItemIndex(-1)}
              onMouseLeave={() => setHoveredNavItemIndex(null)}
              className={`navbar-nav-item-btn ${location.pathname.includes("/social/channels") ? 'active' : ''}`}
              style={{
                display: 'flex',
                color: hoveredNavItemIndex === -1 || location.pathname.includes("/social/channels") ? colors.primary : "rgba(73, 73, 73, 0.6)",
              }}
            >
              <IconComponent
                iconName="group"
                isActive={location.pathname.includes("/social/channels")}
                isHovered={hoveredNavItemIndex === -1}
                colors={colors}
              />
              <span>{t("navbar.Communities") || "Communities"}</span>
            </button>
          )}

          {/* My Pets Button - Only visible on medium screens (769px to 1024px) */}
          {isMediumScreen && (
            <button
              onClick={() => handleNavigation("/dashboard/pets")}
              onMouseEnter={() => setHoveredNavItemIndex(-2)}
              onMouseLeave={() => setHoveredNavItemIndex(null)}
              className={`navbar-nav-item-btn ${location.pathname.includes("/dashboard/pets") || location.pathname.startsWith("/pets/") || location.pathname === "/pets" ? 'active' : ''}`}
              style={{
                display: 'flex',
                color: hoveredNavItemIndex === -2 || location.pathname.includes("/dashboard/pets") || location.pathname.startsWith("/pets/") || location.pathname === "/pets" ? colors.primary : "rgba(73, 73, 73, 0.6)",
              }}
            >
              <IconComponent
                iconName="my-pets"
                isActive={location.pathname.includes("/dashboard/pets") || location.pathname.startsWith("/pets/") || location.pathname === "/pets"}
                isHovered={hoveredNavItemIndex === -2}
                colors={colors}
              />
              <span>{t("navbar.My_Pets") || "My Pets"}</span>
            </button>
          )}

          {/* Mobile Search Icon - Only visible on smaller screens */}
          <div className="navbar-icon-container mobile-search-container">
            <button
              onClick={() => {
                setIsMobileSearchOpen(!isMobileSearchOpen);
                if (!isMobileSearchOpen && searchInputRef.current) {
                  setTimeout(() => searchInputRef.current?.focus(), 100);
                }
              }}
              onMouseEnter={() => setIsMobileSearchIconHovered(true)}
              onMouseLeave={() => setIsMobileSearchIconHovered(false)}
              className="navbar-icon-btn mobile-search-btn"
              title={t("navbar.SearchUser") || "Search"}
            >
              <IconComponent
                iconName="search"
                isHovered={isMobileSearchIconHovered || isMobileSearchOpen}
                colors={colors}
                defaultColor="rgba(73, 73, 73, 0.6)"
              />
            </button>

            {/* Mobile Search Input - Shows below icon when clicked */}
            {isMobileSearchOpen && (
              <div className="mobile-search-input-container">
                <div className="mobile-search-input-wrapper">
                  <IconComponent
                    iconName="search"
                    isHovered={false}
                    colors={colors}
                    defaultColor="rgba(73, 73, 73, 0.6)"
                  />
                  <input
                    ref={searchInputRef}
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onFocus={() => {
                      if (searchQuery || searchResults.length > 0) {
                        setShowSearchDropdown(true);
                      }
                    }}
                    placeholder={t("navbar.SearchUser") || "Search for Users..."}
                    className="mobile-search-input"
                  />
                  {isSearching && (
                    <div
                      className="navbar-search-spinner"
                      style={{
                        borderTopColor: colors.primary,
                      }}
                    />
                  )}
                  <button
                    onClick={() => {
                      setIsMobileSearchOpen(false);
                      setSearchQuery("");
                      setShowSearchDropdown(false);
                    }}
                    className="mobile-search-close-btn"
                  >
                    ×
                  </button>
                </div>

                {/* Mobile Search Results Dropdown - Only show when mobile search is open */}
                {isMobileSearchOpen && showSearchDropdown && (searchResults.length > 0 || isSearching || searchQuery) && (
                  <div
                    ref={mobileSearchDropdownRef}
                    className="mobile-search-dropdown"
                  >
                    {isSearching && searchResults.length === 0 ? (
                      <div className="navbar-search-empty">
                        Searching...
                      </div>
                    ) : filterSearchResults(searchResults, searchQuery).length > 0 ? (
                      filterSearchResults(searchResults, searchQuery).map((result) => (
                        <div
                          key={result.user_id || result.id}
                          onClick={() => {
                            navigate(`/social/profile/${result.user_id || result.id}`);
                            setShowSearchDropdown(false);
                            setSearchQuery("");
                            setIsMobileSearchOpen(false);
                          }}
                          className="navbar-search-result-item"
                        >
                          <img
                            src={result.profile_image_url || result.image || getUserPlaceholderImage(result.gender)}
                            alt={`${result.first_name || ''} ${result.last_name || ''}`.trim() || result.name || result.username}
                            className="navbar-search-result-image"
                            style={{
                                borderColor: getColorScheme(result.user_type).primary,
                            }}
                            onError={(e) => {
                              e.target.src = getUserPlaceholderImage(result.gender);
                            }}
                          />
                          <div style={{ flex: 1 }}>
                            <div className="navbar-search-result-name">
                              {result.full_name || `${result.first_name || ''} ${result.last_name || ''}`.trim() || result.name || result.user_name || result.username}
                            </div>
                          </div>
                        </div>
                      ))
                    ) : searchQuery && !isSearching ? (
                      <div className="navbar-search-empty">
                        No results found
                      </div>
                    ) : null}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Notification Icon */}
          <div className="navbar-icon-container notification-container">
            <button
              onClick={handleNotificationClick}
              onMouseEnter={() => setIsNotificationIconHovered(true)}
              onMouseLeave={() => setIsNotificationIconHovered(false)}
              className="navbar-icon-btn"
              title={t("navbar.Notifications")}
            >
              {isFetchingNotifications ? (
                <div
                  className="navbar-notification-spinner"
                  style={{
                    borderTopColor: colors.primary,
                  }}
                />
              ) : (
                <IconComponent
                  iconName="notification"
                  isHovered={isNotificationIconHovered}
                  colors={colors}
                  defaultColor="rgba(73, 73, 73, 0.6)"
                />
              )}
              {/* Notification badge - show for pending follow requests */}
              {(
                userCounts?.notifications > 0) && (
                  <div className="navbar-badge">
                    {userCounts?.notifications}
                  </div>
                )}
            </button>

            {/* Notification Dropdown - Desktop only */}
            {!isMobile && isNotificationOpen && (
              <div
                className="dropdown-overlay"
                onClick={() => setIsNotificationOpen(false)}
              />
            )}
            {!isMobile && (
              <NotificationDropdown
                isOpen={isNotificationOpen}
                onClose={() => {
                  setIsNotificationOpen(false);
                  setHighlightNotificationId(null);
                }}
                userType={userType}
                colors={colors}
                // onRequestHandled={fetchNotifications}
                updateNotificationCount={updateNotificationCount}
                highlightNotificationId={highlightNotificationId}
              />
            )}
          </div>

          {/* Chat Icon */}
          <div className="navbar-icon-container">
            {(() => {
              const isChatActive = location.pathname.startsWith("/chat") || location.pathname.includes("/chat");
              return (
                <button
                  onClick={() => {
                    if (isMobile) {
                      // Navigate to chat page on mobile
                      navigate('/chat');
                    } else {
                      // Show dropdown on desktop
                      const willBeOpen = !isChatDropdownOpen;
                      setIsNotificationOpen(false);
                      setIsChatDropdownOpen(willBeOpen);
                    }
                  }}
                  onMouseEnter={() => setIsChatIconHovered(true)}
                  onMouseLeave={() => setIsChatIconHovered(false)}
                  className={`navbar-icon-btn ${isChatActive ? 'active' : ''}`}
                  title="Messages"
                >
                  <IconComponent
                    iconName="chat"
                    isActive={isChatActive}
                    isHovered={isChatIconHovered}
                    colors={colors}
                    defaultColor="rgba(73, 73, 73, 0.6)"
                  />
                </button>
              );
            })()}

            {/* Chat Badge */}
            {(() => {
              const chatBadgeCount = parseInt(
                localStorage.getItem("chat_badge_count") || "0"
              );
              return chatBadgeCount > 0 ? (
                <div className="navbar-badge navbar-chat-badge">
                  {chatBadgeCount > 99 ? "99+" : chatBadgeCount}
                </div>
              ) : null;
            })()}

            {/* Chat Dropdown - Desktop only */}
            {!isMobile && isChatDropdownOpen && (
              <div
                className="dropdown-overlay"
                onClick={() => setIsChatDropdownOpen(false)}
              />
            )}
            {!isMobile && (
              <ChatDropdown
                isOpen={isChatDropdownOpen}
                onClose={() => setIsChatDropdownOpen(false)}
                colors={colors}
              />
            )}
          </div>
          {/* AI Chatbot Icon */}
          {/* Hidden for now*/}
          <div className="navbar-icon-container navbar-ai-chat-icon">
            <button
              onClick={handleAIChatToggle}
              onMouseEnter={() => setIsAIChatIconHovered(true)}
              onMouseLeave={() => setIsAIChatIconHovered(false)}
              className={`navbar-icon-btn ${isAIChatOpen ? 'active' : ''}`}
              title={t("navbar.AI_Chat") || "AI Assistant"}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: "0",
                background: "transparent",
                border: "none",
                cursor: "pointer",
              }}
            >
              <img
                src="/images/yana-logo-nobg.png"
                alt="AI Assistant"
                style={{
                  width: "28px",
                  height: "28px",
                  objectFit: "contain",
                  opacity: isAIChatOpen ? 1 : 0.6,
                  transition: "opacity 0.2s ease",
                }}
              />
            </button>
          </div>


          {/* Shopping Cart Icon - Commented out for now - will be used later */}
          {/* 
          <button
            onClick={() => handleNavigation(`/dashboard/${userType}/cart`)}
            onMouseEnter={() => setIsCartIconHovered(true)}
            onMouseLeave={() => setIsCartIconHovered(false)}
            style={{
              background: "transparent",
              border: "none",
              cursor: "pointer",
              padding: "8px",
              borderRadius: "8px",
              transition: "all 0.2s ease",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              position: "relative",
            }}
            title="Shopping Cart"
          >
            <IconComponent
              iconName="shopping-cart"
              isHovered={isCartIconHovered}
              colors={colors}
              defaultColor="rgba(73, 73, 73, 0.6)"
            />
            {userCounts?.cartItems > 0 && (
              <div
                style={{
                  position: "absolute",
                  top: "4px",
                  right: "4px",
                  background: colors.primary,
                  color: "white",
                  borderRadius: "50%",
                  minWidth: "18px",
                  height: "18px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "11px",
                  fontWeight: "600",
                  fontFamily:
                    '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                  boxShadow: "0 2px 4px rgba(0, 0, 0, 0.2)",
                  border: "2px solid #ffffff",
                  zIndex: 10,
                }}
              >
                {userCounts.cartItems > 99 ? "99+" : userCounts.cartItems}
              </div>
            )}
          </button>
          */}

          {/* Profile */}
          <div className="navbar-profile-container profile-container">
            <button
              onClick={() => setIsProfileOpen(!isProfileOpen)}
              className="navbar-profile-btn"
            >
              <img
                src={profileImageUrl || getUserPlaceholderImage(user?.gender)}
                alt="User Profile"
                width={60}
                height={60}
                className="navbar-profile-image"
                style={{
                  borderColor: colors.primary,
                }}
                onError={(e) => {
                  e.target.src = getUserPlaceholderImage(user?.gender);
                }}
              />
            </button>

            {/* Profile Dropdown */}
            {isProfileOpen && (
              <>
                <div
                  className="dropdown-overlay"
                  onClick={() => setIsProfileOpen(false)}
                />
                <div className="navbar-profile-dropdown profile-dropdown">
                  {/* User Info */}
                  <div
                    className="navbar-profile-user-info"
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      textAlign: "center",
                    }}
                  >
                    <div
                      className="navbar-profile-user-name"
                      style={{
                        fontSize: "15px",
                        fontWeight: 600,
                      }}
                    >
                      {displayName}
                    </div>
                    <div
                      className="navbar-profile-user-type"
                      style={{
                        color: colors.primary,
                        fontSize: "13px",
                        fontWeight: 500,
                        marginTop: "2px",
                      }}
                    >
                      {userType
                        .replace("_", " ")
                        .toLowerCase()
                        .replace(/\b\w/g, (l) => l.toUpperCase())}
                    </div>
                    {/* View Profile button directly under user type */}
                    <button
                      onClick={() => {
                        navigate("/social/profile");
                        setIsProfileOpen(false);
                      }}
                      className="navbar-profile-view-btn"
                      style={{
                        marginTop: "10px",
                        padding: "9px 28px",
                        borderRadius: "999px",
                        border: `1px solid ${colors.primary}`,
                        background: "transparent",
                        color: colors.primary,
                        fontSize: "13.5px",
                        fontWeight: 500,
                        cursor: "pointer",
                        alignSelf: "center",
                        minWidth: "180px",
                      }}
                    >
                      {t("navbar.View_Profile") || "View Profile"}
                    </button>
                  </div>

                  <button
                    onClick={() => {
                      navigate("/account/preferences");
                      setIsProfileOpen(false);
                    }}
                    className="navbar-dropdown-menu-item"
                  >
                    <IconComponent
                      iconName="settings-gear"
                      isActive={false}
                      colors={colors}
                      defaultColor="#494949"
                    />
                    {t("navbar.Account_Preferences")}
                  </button>

                  {/* Language Toggle in Dropdown */}
                  <button
                    onClick={() => {
                      const newLang = i18n.language === "en" ? "ar" : "en";
                      i18n.changeLanguage(newLang);
                      setIsProfileOpen(false);
                    }}
                    className="navbar-dropdown-menu-item"
                  >
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="#494949"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="navbar-dropdown-menu-item-icon"
                    >
                      <circle cx="12" cy="12" r="10" />
                      <path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
                    </svg>
                    <span>{i18n.language === "en" ? "Arabic" : "English"}</span>
                    <span className="navbar-dropdown-menu-item-text">
                      {i18n.language === "en" ? "AR" : "EN"}
                    </span>
                  </button>

                  <button
                    onClick={() => {
                      setIsSupportModalOpen(true);
                      setIsProfileOpen(false);
                    }}
                    className="navbar-dropdown-menu-item"
                  >
                    <IconComponent
                      iconName="support"
                      isActive={false}
                      colors={colors}
                      defaultColor="#494949"
                    />
                    {t("navbar.Support")}
                  </button>

                  <button
                    onClick={handleLogoutClick}
                    className="navbar-dropdown-menu-item"
                  >
                    <IconComponent
                      iconName="log-out"
                      isActive={false}
                      colors={colors}
                      defaultColor="#494949"
                    />
                    {t("navbar.Log_Out")}
                  </button>
                </div>
              </>
            )}
          </div>
          {/* Hamburger menu button for mobile (moved to far right) */}
          <button
            className="navbar-menu-btn"
            onClick={handleHamburgerClick}
            aria-label="Open menu"
          >
            <span>☰</span>
          </button>

          {/* Mobile menu container: add ref for outside click detection */}
          {isMobileMenuOpen && (
            <div
              ref={mobileMenuRef}
              className="mobile-menu-container"
            >
              {navigationItems.map((item, index) => {
                // My Pets active state logic - Commented out for now - moved to social sidebar
                // Special logic for "My Pets" - should be active on all pet-related pages
                let isActive = false;
                const pathname = location.pathname;

                // Explicitly check for social-media first and exclude medical paths
                if (item.icon === "social-media") {
                  // Only active on exact /social path or paths starting with /social/
                  // Explicitly exclude ALL medical paths - this must be false on any medical page
                  const isOnSocialPage = pathname === "/social" || pathname.startsWith("/social/");
                  const isOnMedicalPage = pathname.startsWith("/medical");
                  isActive = isOnSocialPage && !isOnMedicalPage;
                } else if (item.icon === "medical-care") {
                  // Active if on medical-care path OR any clinic operations route
                  isActive = pathname.startsWith(item.path) ||
                    pathname.startsWith("/medical/clinic/appointment-management") ||
                    pathname.startsWith("/medical/clinic/chat") ||
                    pathname.startsWith("/reception") ||
                    (pathname.startsWith("/medical/clinic") &&
                      (pathname.includes("/appointment-management") ||
                        pathname.includes("/chat") ||
                        pathname.includes("/dashboard"))) ||
                    pathname.startsWith("/medical/dashboard");
                } else {
                  // Use startsWith for more precise matching instead of includes
                  isActive = pathname === item.path || pathname.startsWith(item.path + "/");
                }
                return (
                  <button
                    key={index}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleNavigation(item.path);
                      setIsMobileMenuOpen(false);
                    }}
                    className={`mobile-menu-item ${isActive ? 'active' : ''}`}
                    style={{
                      color: isActive ? colors.primary : "#494949",
                    }}
                  >
                    <IconComponent
                      iconName={item.icon}
                      isActive={isActive}
                      colors={colors}
                    />
                    <span>{item.label}</span>
                  </button>
                );
              })}

              {/* Communities Button - Only visible on mobile (768px and below) */}
              {isMobile && (
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleNavigation("/social/channels");
                    setIsMobileMenuOpen(false);
                  }}
                  className={`mobile-menu-item ${location.pathname.includes("/social/channels") ? 'active' : ''}`}
                  style={{
                    color: location.pathname.includes("/social/channels") ? colors.primary : "#494949",
                  }}
                >
                  <IconComponent
                    iconName="group"
                    isActive={location.pathname.includes("/social/channels")}
                    colors={colors}
                  />
                  <span>{t("navbar.Communities") || "Communities"}</span>
                </button>
              )}

              {/* My Pets Button - Only visible on mobile (768px and below) */}
              {isMobile && (
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleNavigation("/dashboard/pets");
                    setIsMobileMenuOpen(false);
                  }}
                  className={`mobile-menu-item ${location.pathname.includes("/dashboard/pets") || location.pathname.startsWith("/pets/") || location.pathname === "/pets" ? 'active' : ''}`}
                  style={{
                    color: location.pathname.includes("/dashboard/pets") || location.pathname.startsWith("/pets/") || location.pathname === "/pets" ? colors.primary : "#494949",
                  }}
                >
                  <IconComponent
                    iconName="my-pets"
                    isActive={location.pathname.includes("/dashboard/pets") || location.pathname.startsWith("/pets/") || location.pathname === "/pets"}
                    colors={colors}
                  />
                  <span>{t("navbar.My_Pets") || "My Pets"}</span>
                </button>
              )}

              {/* AI Chat Shortcut - only for very small screens where the icon is hidden */}
              {isUltraSmallScreen && (
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleAIChatToggle();
                    setIsMobileMenuOpen(false);
                  }}
                  className={`mobile-menu-item ${isAIChatOpen ? 'active' : ''}`}
                  style={{
                    color: isAIChatOpen ? colors.primary : "#494949",
                  }}
                >
                  <img
                    src="/images/yana-logo-nobg.png"
                    alt="AI Assistant"
                    style={{
                      width: "28px",
                      height: "28px",
                      objectFit: "contain",
                    }}
                  />
                  <span>{t("navbar.AI_Chat") || "AI Assistant"}</span>
                </button>
              )}
            </div>
          )}
        </div>
      </nav>

      {/* Search Modal */}
      <SearchModal
        isOpen={isSearchModalOpen}
        onClose={() => setIsSearchModalOpen(false)}
        userType={userType}
      />

      {/* Logout Confirmation Modal */}
      <LogoutConfirmationModal
        isOpen={isLogoutModalOpen}
        onClose={() => setIsLogoutModalOpen(false)}
        onConfirm={handleLogout}
        userType={userType}
      />

      {/* AI Chat Widget */}
      <AIChatWidget
        isOpen={isAIChatOpen}
        onClose={() => {
          setIsAIChatOpen(false);
          setIsAIChatMinimized(false);
        }}
        isMinimized={isAIChatMinimized}
        onMinimize={() => setIsAIChatMinimized(!isAIChatMinimized)}
        userTypeColor={colors.primary}
        userType={userType}
      />

      {/* Support Modal */}
      <SupportModal
        isOpen={isSupportModalOpen}
        onClose={() => setIsSupportModalOpen(false)}
      />
      {/* Enhanced Responsive styles for navbar */}
      <style>{`
        /* Dropdown overlay to ensure proper z-index */
        .dropdown-overlay {
          position: fixed !important;
          top: 0 !important;
          left: 0 !important;
          right: 0 !important;
          bottom: 0 !important;
          z-index: 9998 !important;
          background: transparent !important;
        }

        /* Mobile overlay with semi-transparent background - for other dropdowns */
        @media (max-width: 768px) {
          .dropdown-overlay {
            background: rgba(0, 0, 0, 0.4) !important;
            backdrop-filter: blur(2px);
          }
        }

        /* All dropdowns with highest z-index - above chat page elements */
        .navbar-dropdown,
        .profile-dropdown {
          z-index: 100000 !important;
          position: fixed !important;
        }

        /* Notification and Chat dropdowns specific styling - Desktop only */
        .notification-dropdown {
          z-index: 9999 !important;
          position: fixed !important;
        }

        /* Ensure chat dropdown appears above notifications */
        .chat-dropdown {
          z-index: 10000 !important;
          position: fixed !important;
        }

        /* Ensure notification container has proper stacking */
        .notification-container {
          z-index: 9999 !important;
          position: relative !important;
        }

        /* Responsive positioning for dropdowns - removed inline overrides */
        /* Responsive styles are now handled in the component CSS files */

        /* Large screens - show all navigation items */
        @media (min-width: 1501px) {
          .navbar-menu-btn {
            display: none !important;
          }
          .navbar-dropdown {
            display: none !important;
          }
          .navbar-desktop-items {
            gap: clamp(16px, 2.5vw, 28px) !important;
            max-width: 1000px !important;
          }
        }

        /* Medium screens - still show navigation if there's space */
        @media (min-width: 1025px) and (max-width: 1500px) {
          .navbar-menu-btn {
            display: none !important;
          }
          .navbar-dropdown {
            display: none !important;
          }
          .navbar-desktop-items {
            gap: clamp(8px, 1.8vw, 20px) !important;
            max-width: 900px !important;
          }
          .nav-item-btn {
            font-size: 11px !important;
            padding: 6px 4px !important;
          }
        }

        /* Tablet screens (768px - 1024px) - show navigation items with compact spacing */
        @media (min-width: 769px) and (max-width: 1024px) {
          .navbar-desktop-items {
            display: none !important;
          }
          .navbar-search-container {
            display: none !important;
          }
          .navbar-menu-btn {
            display: none !important;
          }
          .navbar-dropdown {
            display: none !important;
          }
          .hide-on-mobile {
            display: flex !important;
          }
          .navbar-nav-item-btn {
            font-size: 10px !important;
            padding: 5px 3px !important;
          }
          .navbar-nav-item-btn span {
            display: none !important;
          }
          .navbar-icon-btn-text {
            display: none !important;
          }
          .mobile-search-btn {
            display: flex !important;
          }
        }

        /* Smaller screens - hide nav items, show hamburger and mobile search */
        @media (max-width: 768px) {
          .navbar-desktop-items {
            display: none !important;
          }
          .navbar-search-container {
            display: none !important;
          }
          .navbar-menu-btn {
            display: inline-flex !important;
          }
          .mobile-search-btn {
            display: flex !important;
          }
          /* Hide navigation items (Sulhafa Connect, Medical Care) when hamburger is visible */
          .hide-on-mobile {
            display: none !important;
          }
          .navbar-nav-item-btn span {
            display: none !important;
          }
          .navbar-icon-btn-text {
            display: none !important;
          }
          .navbar-dropdown {
            right: clamp(8px, 2vw, 16px) !important;
            top: 75px !important;
            min-width: 200px !important;
            border: 1px solid #E5E7EB !important;
          }
        }

        /* Tablet screens - optimize spacing */
        @media (max-width: 768px) {
          nav {
            padding: 14px clamp(12px, 3vw, 24px) !important;
            min-height: 75px !important;
          }
          .navbar-right-icons {
            gap: clamp(6px, 1.5vw, 12px) !important;
          }
          .navbar-right-icons > button,
          .navbar-right-icons > div {
            transform: scale(0.9);
          }
          .navbar-dropdown {
            top: 65px !important;
            right: 8px !important;
            min-width: 180px !important;
          }
        }

        /* Small mobile screens (425px and below) - extra compact design */
        @media (max-width: 425px) {
          nav {
            padding: 10px 12px !important;
            min-height: 65px !important;
          }
          .navbar-right-icons {
            gap: 6px !important;
          }
          .navbar-right-icons > button,
          .navbar-right-icons > div {
            transform: scale(0.8);
          }
          .navbar-icon-btn {
            padding: 6px !important;
          }
          .navbar-menu-btn {
            padding: 6px !important;
            transform: scale(0.9);
          }
          .navbar-dropdown {
            top: 58px !important;
            right: 6px !important;
            min-width: calc(100vw - 12px) !important;
            max-width: calc(100vw - 12px) !important;
            font-size: 13px !important;
          }
          .profile-dropdown {
            right: 6px !important;
            min-width: calc(100vw - 12px) !important;
            max-width: calc(100vw - 12px) !important;
            font-size: 13px !important;
          }
          .navbar-ai-chat-icon {
            display: none !important;
          }
        }

        /* Mobile screens - compact design */
        @media (max-width: 480px) {
          nav {
            padding: 12px 16px !important;
            min-height: 70px !important;
          }
          .navbar-right-icons {
            gap: 8px !important;
          }
          .navbar-right-icons > button,
          .navbar-right-icons > div {
            transform: scale(0.85);
          }
          .navbar-dropdown {
            top: 60px !important;
            right: 8px !important;
            min-width: 170px !important;
            font-size: 13px !important;
          }
          .profile-dropdown {
            right: 8px !important;
            min-width: 160px !important;
            font-size: 13px !important;
          }
        }

        /* Ultra-small screens - hide some icons and make others smaller */
        @media (max-width: 360px) {
          .navbar-right-icons > button,
          .navbar-right-icons > div {
            transform: scale(0.75);
          }
          .navbar-right-icons {
            gap: 4px !important;
          }
          /* Hide chat icon on very small screens */
          .navbar-right-icons > button:nth-child(4) {
            display: none !important;
          }
        }

        /* Prevent text selection and improve UX */
        .navbar-desktop-items {
          user-select: none;
        }

        .nav-item-btn {
          flex-shrink: 0;
          user-select: none;
        }

        /* Smooth transitions for responsive changes */
        .navbar-right-icons > * {
          transition: transform 0.2s ease;
        }

        /* Ensure dropdowns don't overflow viewport */
        .navbar-dropdown,
        .profile-dropdown {
          max-width: calc(100vw - 32px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
        }
      `}</style>
    </>
  );
});

Navbar.displayName = 'Navbar';

export default Navbar;

